import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  SunIcon,
  MoonIcon,
  TrashIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const SettingsPanel = ({ user }) => {
  const { theme, setTheme, toggleTheme } = useTheme();
  const [settings, setSettings] = useState({
    notifications: true,
    language: 'en',
    timezone: 'UTC',
    autoSave: true,
    dataSaver: false,
    analytics: true
  });

  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('jarvis-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
  }, []);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = () => {
    try {
      localStorage.setItem('jarvis-settings', JSON.stringify(settings));
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  const SettingToggle = ({ setting, label, description }) => (
    <div className="flex items-center justify-between py-4">
      <div>
        <label className="text-sm font-medium text-gray-900 dark:text-dark-primary">{label}</label>
        {description && (
          <p className="text-sm text-gray-500 dark:text-dark-secondary">{description}</p>
        )}
      </div>
      <button
        onClick={() => updateSetting(setting, !settings[setting])}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
          settings[setting] ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            settings[setting] ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const ThemeOption = ({ value, icon: Icon, label, description }) => (
    <button
      onClick={() => setTheme(value)}
      className={`flex items-start p-4 rounded-lg border-2 transition-all duration-200 w-full ${
        theme === value
          ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/50 dark:border-blue-400'
          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
    >
      <Icon className={`w-6 h-6 mr-3 mt-0.5 flex-shrink-0 ${
        theme === value 
          ? 'text-blue-600 dark:text-blue-400' 
          : 'text-gray-500 dark:text-gray-400'
      }`} />
      <div className="text-left">
        <div className={`font-medium ${
          theme === value 
            ? 'text-blue-700 dark:text-blue-300' 
            : 'text-gray-900 dark:text-dark-primary'
        }`}>
          {label}
        </div>
        <div className={`text-sm mt-1 ${
          theme === value 
            ? 'text-blue-600 dark:text-blue-400' 
            : 'text-gray-500 dark:text-dark-secondary'
        }`}>
          {description}
        </div>
      </div>
      {theme === value && (
        <div className="ml-auto">
          <CheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-primary">Settings</h2>
        <p className="text-gray-600 dark:text-dark-secondary">Customize your Jarvis AI experience</p>
      </div>

      {/* User Profile */}
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow border border-gray-200 dark:border-dark-primary p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-dark-primary mb-4">Profile Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-secondary mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={user?.full_name || ''}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400"
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-secondary mb-1">
              Email
            </label>
            <input
              type="email"
              value={user?.email || ''}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Appearance */}
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow border border-gray-200 dark:border-dark-primary p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-primary">Appearance</h3>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Quick toggle theme"
          >
            {theme === 'dark' ? (
              <SunIcon className="w-5 h-5 text-yellow-500" />
            ) : (
              <MoonIcon className="w-5 h-5 text-blue-600" />
            )}
          </button>
        </div>
        
        <div className="space-y-3">
          <ThemeOption
            value="light"
            icon={SunIcon}
            label="Light"
            description="Clean, bright interface perfect for daytime use"
          />
          <ThemeOption
            value="dark"
            icon={MoonIcon}
            label="Dark"
            description="Easy on the eyes, great for low-light environments"
          />
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow border border-gray-200 dark:border-dark-primary p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-dark-primary mb-4">Preferences</h3>
        <div className="divide-y divide-gray-200 dark:divide-gray-600">
          <SettingToggle
            setting="notifications"
            label="Notifications"
            description="Receive notifications for important updates"
          />
          <SettingToggle
            setting="autoSave"
            label="Auto Save"
            description="Automatically save your conversations"
          />
          <SettingToggle
            setting="dataSaver"
            label="Data Saver Mode"
            description="Reduce bandwidth usage"
          />
          <SettingToggle
            setting="analytics"
            label="Usage Analytics"
            description="Help improve Jarvis by sharing usage data"
          />
        </div>
      </div>

      {/* Language & Region */}
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow border border-gray-200 dark:border-dark-primary p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-dark-primary mb-4">Language & Region</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-secondary mb-1">
              Language
            </label>
            <select
              value={settings.language}
              onChange={(e) => updateSetting('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-secondary mb-1">
              Timezone
            </label>
            <select
              value={settings.timezone}
              onChange={(e) => updateSetting('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-dark-primary focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-white dark:bg-dark-secondary rounded-lg shadow border border-gray-200 dark:border-dark-primary p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-dark-primary mb-4">Data Management</h3>
        <div className="space-y-4">
          <button className="w-full md:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-dark-secondary hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Export My Data
          </button>
          <button 
            onClick={clearAllData}
            className="w-full md:w-auto px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <TrashIcon className="w-4 h-4 inline mr-2" />
            Clear All Data
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end items-center space-x-3">
        {saveStatus === 'saved' && (
          <div className="flex items-center text-green-600 dark:text-green-400 text-sm">
            <CheckIcon className="w-4 h-4 mr-1" />
            Settings saved
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="text-red-600 dark:text-red-400 text-sm">
            Failed to save settings
          </div>
        )}
        <button 
          onClick={saveSettings}
          className="btn-primary"
        >
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default SettingsPanel;